/* §6.7 — THE LIBRARY'S LIST VIEW AS A SORTABLE TABLE. His word, 2026-09-18 ~22:10, on Everfit's
 * library screenshot: *"plus we can order by any of these options. so make it same or even better
 * than this!"*
 *
 * ⚠️ THE COLUMNS ARE OUR FACTS, NOT THEIRS, AND THAT IS THE "OR EVEN BETTER". Their header is
 * `☐ · Exercises (2649) · Tags · Modality · Muscle Group · Movement Pattern · Category`, and the
 * `Tags` column on the row he screenshotted reads `--`. An always-empty column is the thing that
 * reads amateur in theirs, so copying it faithfully would copy the defect: there is no `Tags`
 * column here until tags exist. What replaces it is two facts a coach can ACT on and Everfit does
 * not show — the difficulty, and whether a row RECORDS ANYTHING AT ALL.
 *
 * ⚠️ **CORRECTED 2026-09-22: THIS COMMENT USED TO SAY "the difficulty THE SWAP PAGE READS", AND
 * THAT IS FALSE.** MEASURED: `SwapSuggestion.ranked` takes `[Exercise]` — the CANONICAL catalogue
 * type — and `difficultyGap` reads `Exercise.difficultyLevel`, i.e. `exercises.difficulty_level`,
 * which is rated on all 472 published rows and spread evenly 1–10. **`coach_exercises.difficulty`
 * has no engine reader at all**: this table and the iOS coach form are the only things that touch
 * it. So it is a fact a coach RECORDS, not one the engine consumes, and nothing downstream moves
 * when it changes.
 *
 * ⚠️ AND THE PARAGRAPH ABOVE CONVICTS ITSELF, which is why the correction is worth this much
 * space. It rejects `Tags` because "an always-empty column is the thing that reads amateur" — and
 * MEASURED the same day, `coach_exercises.difficulty` is NULL on **all 331 rows across all three
 * coach libraries**. The column it argued for was the column it argued against. Filed as W58;
 * 112 of the 331 can be derived from the canonical rating by name, the rest are a coach's to set.
 *
 * ⚠️ SORTING IS PURE AND LIVES HERE, not in the screen, for the reason §6.7 rules: a test executes
 * each column instead of a render asserting it, and the URL can carry a sort a coach links to.
 * FILTERING IS A SEPARATE FUNCTION, deliberately — a single function doing both has to decide
 * which wins when they disagree, and the answer is always "neither, they compose".
 */
import { byName, flip, nullsLast, numeric, type SortDir } from "./sortOrder";

export type { SortDir };

/** One row of the library, as the table reads it. */
export type LibraryRow = {
  id: string;
  name: string;
  /** The eleven-value word, or null when the row predates the CHECK or holds a category. */
  movementPattern: string | null;
  /** Canonical primary muscles. `primary_muscles` is FREE TEXT by design — see `muscles.ts`. */
  muscles: string[];
  equipment: string[];
  /** 1–10, the same scale the swap page reads. `null` is UNRATED and never a fake 5. */
  difficulty: number | null;
  /** W24(d) — `coach_exercises.category`, one of five or NULL ("not categorised" is an answer). */
  category: string | null;
  /** What a set of this records. EMPTY is the finding, not a blank. */
  trackingFields: string[];
  /** ISO date the coach last had this in an assigned workout, or null for never. */
  lastUsedISO: string | null;
  demoVideoUrl: string | null;
  thumbnailUrl: string | null;
};

export const sortKeys = [
  "name", "pattern", "muscles", "equipment", "difficulty", "records", "lastUsed",
] as const;
export type SortKey = (typeof sortKeys)[number];

/**
 * Each column's DEFAULT direction — the question a coach is asking when they click it.
 *
 * ⚠️ NOT ALL ASCENDING, and the roster made this argument first: clicking `Last used` to be shown
 * what you used yesterday is not a question anybody has. A coach clicks it to find what they have
 * STOPPED reaching for — a library's dead weight — so it leads with the oldest. `Records` leads
 * with the rows that record NOTHING, which is the column's whole reason for existing: those are
 * the rows the phone cannot log.
 */
export const defaultDir: Record<SortKey, SortDir> = {
  name: "asc",
  pattern: "asc",
  muscles: "asc",
  equipment: "asc",
  difficulty: "asc",   // easiest first
  records: "asc",      // fewest first, so "records nothing" leads
  lastUsed: "asc",     // oldest first, so the forgotten rows lead
};


/**
 * A row that records nothing — §6.7's own finding, and the reason the column is not decorative.
 *
 * `tracking_fields` is what the phone writes a set into. An empty one is a row a client can be
 * prescribed and cannot log: the workout shows it, they do it, and nothing is saved. It is
 * invisible everywhere else in this app, which is why it earns a column and a warning dot.
 */
export function recordsNothing(row: LibraryRow): boolean {
  return row.trackingFields.length === 0;
}

/**
 * The value a LIST column sorts on: its first entry, alphabetically.
 *
 * ⚠️ AN EMPTY LIST IS ABSENT, NOT AN EMPTY STRING — `""` would sort FIRST ascending, so every row
 * with no muscles recorded would lead the *by muscle* view. Absent sorts last, like everything
 * else that cannot be answered for.
 */
function firstOf(list: readonly string[]): string | null {
  if (list.length === 0) return null;
  return [...list].sort(byName)[0] ?? null;
}

/** A stable, total order for one column. Absent values sort LAST whichever way the column runs. */
export function compareRows(a: LibraryRow, b: LibraryRow, key: SortKey, dir: SortDir): number {
  const f = flip(dir);
  switch (key) {
    case "name":
      return byName(a.name, b.name) * f;
    case "pattern":
      return bothPresent(a.movementPattern, b.movementPattern, byName, f);
    case "muscles":
      return listCompare(a.muscles, b.muscles, f);
    case "equipment":
      return listCompare(a.equipment, b.equipment, f);
    case "difficulty":
      return bothPresent(a.difficulty, b.difficulty, numeric, f);
    case "records":
      /* COUNT, not presence — a row recording reps alone and one recording reps+kg+time are
         different amounts of truth, and the coach sorting this column is ranking exactly that.
         Zero is a real count here (it IS the finding), so this column has no absent case. */
      return numeric(a.trackingFields.length, b.trackingFields.length) * f;
    case "lastUsed":
      return bothPresent(a.lastUsedISO, b.lastUsedISO, byName, f);
  }
}

/* ⚠️ THE FLIP IS APPLIED ONLY WHEN BOTH SIDES ARE PRESENT. Multiplying the null branch by the
   direction is the one mistake `sortOrder.ts` exists to prevent, and writing it out twice inline
   is how it would creep back — so both shapes are one helper each. */
function bothPresent<T>(
  a: T | null, b: T | null, cmp: (x: T, y: T) => number, f: 1 | -1,
): number {
  if (a === null || b === null) return nullsLast(a, b, cmp);
  return cmp(a, b) * f;
}

function listCompare(a: readonly string[], b: readonly string[], f: 1 | -1): number {
  const x = firstOf(a);
  const y = firstOf(b);
  if (x === null || y === null) return nullsLast(x, y, byName);
  return byName(x, y) * f;
}

/** Sort a list by one column. NOT in place, and STABLE within equal keys. */
export function sortRows(rows: LibraryRow[], key: SortKey, dir: SortDir): LibraryRow[] {
  return [...rows].sort((a, b) => compareRows(a, b, key, dir));
}

/** Read a sort out of a route query, refusing anything it does not recognise. */
export function sortFromQuery(q: Record<string, string>): { key: SortKey; dir: SortDir } {
  const key = sortKeys.find((k) => k === q.sort);
  if (key === undefined) return { key: "name", dir: defaultDir.name };
  // An unrecognised direction falls to the COLUMN'S own default, never to "asc" —
  // `?sort=lastUsed&dir=banana` must not quietly lead with what was used yesterday.
  const dir: SortDir = q.dir === "asc" || q.dir === "desc" ? q.dir : defaultDir[key];
  return { key, dir };
}

/* ── W24(a) — THE VIEW IN THE URL: THE SORT **AND** THE ONE FILTER THAT IS A TOGGLE ───────────
 *
 * `EVERFIT-MAP` §18-quater depth 3, read live: pressing a column head rewrote their address to
 * `?page=1&sorter=modality&sort=1&from=false%2Cfalse&video_only=false`. **Their filter state is
 * in the address on this screen**, which is more than their own Workouts library does and more
 * than we did anywhere.
 *
 * 🔴 **THE SORT AND THE TOGGLE ARE ONE VALUE, NOT TWO `useQuerySort`s.** Two readers over one
 * hash can answer from two different reads of it — the calendar's own W22(d) lesson, written
 * down the same day — and a bad half must not poison the good one.
 *
 * ⚠️ **`video_only` IS THEIR SPELLING AND IS KEPT**, because the point of a URL parameter is that
 * somebody else's link works; renaming it to `hasVideo` would be our vocabulary in a place whose
 * whole job is to be quoted.
 *
 * ⚠️ **`from` IS NOT COPIED AND IS NOT INVENTED.** It is a PAIR matching their filter's two
 * `Exercises from` checkboxes — `Everfit` and `Custom Exercises` — and every row in our catalogue
 * is the coach's own, so the concept has no referent here. Filed in W24(a), not faked.
 *
 * ⚠️ **AND `sorter`/`sort=1|-1` ARE NOT COPIED EITHER.** Ours were already `sort`/`dir` with
 * WORDS, in the URL, before this dig; changing the spelling would break every link a coach has
 * already sent for no gain a coach can see. The COPY is "the filter belongs in the address"; the
 * spelling of our own two keys is not the thing being copied.
 */
export type LibraryView = {
  key: SortKey;
  dir: SortDir;
  /** Their `video_only`. */
  videoOnly: boolean;
  /** W24(b) — their `page`, ONE-BASED as theirs is (`1 - 50 of 2649`). */
  page: number;
};

export function viewFromQuery(q: Record<string, string>): LibraryView {
  const { key, dir } = sortFromQuery(q);
  /* ⚠️ ONLY THE LITERAL `true` TURNS IT ON. `Boolean("false")` is `true` in JavaScript, which is
     how a link carrying their own default (`video_only=false`) would have silently narrowed a
     coach's library to the rows that have a video. */
  /* ⚠️ AND AN UNPARSEABLE PAGE IS **1**, NOT `NaN`. `Number("banana")` is `NaN`, and `NaN` slices
     an array to nothing — a pasted link with a typo would have shown a coach an empty library
     and no reason for it. */
  const asked = Number(q.page);
  const page = Number.isFinite(asked) && asked >= 1 ? Math.floor(asked) : 1;
  return { key, dir, videoOnly: q.video_only === "true", page };
}

/** The query string for a view — the ONE place these keys are spelled. */
export function libraryQuery(v: LibraryView): string {
  const p = new URLSearchParams({ sort: v.key, dir: v.dir });
  /* ⚠️ THE DEFAULT IS OMITTED, not written as `false`. A link is read by a human as well as by a
     parser, and `?sort=name&dir=asc` says what it does; theirs carries `from=false%2Cfalse` and
     `video_only=false` on every press, which is three of five keys saying nothing. */
  if (v.videoOnly) p.set("video_only", "true");
  if (v.page > 1) p.set("page", String(v.page));
  return `?${p.toString()}`;
}

/* ── W24(b) — THE PAGER ──────────────────────────────────────────────────────────────────────
 *
 * `EVERFIT-MAP` §18-quater depth 1: their foot is `‹  1 - 50 of 2649  ›`. Ours rendered the
 * whole table. His library is 142 rows today so nothing hurts yet — the shape is theirs and the
 * number is worth copying before a coach's library grows.
 *
 * ⚠️ **THE PAGE IS CLAMPED TO WHAT EXISTS, AND THE CLAMP IS WHERE THE SLICE IS, NOT WHERE THE
 * URL IS READ.** A coach on page 3 who then narrows the filter to 12 rows is on a page that no
 * longer exists; showing them an empty table under a `Filter · on` label is a screen that looks
 * broken for a reason it will not say. They land on the last page that has rows.
 *
 * ⚠️ **AND AN EMPTY LIBRARY IS PAGE 1 OF 1, NEVER PAGE 0.** The label is what a human reads.
 */
export const PAGE_SIZE = 50;

export function lastPage(total: number): number {
  return Math.max(1, Math.ceil(total / PAGE_SIZE));
}

export function clampPage(page: number, total: number): number {
  if (!Number.isFinite(page)) return 1;
  return Math.min(Math.max(1, Math.floor(page)), lastPage(total));
}

/** The rows of one page. `page` is clamped here too, so a caller cannot slice past the end. */
export function pageRows<T>(rows: readonly T[], page: number): T[] {
  const p = clampPage(page, rows.length);
  const from = (p - 1) * PAGE_SIZE;
  return rows.slice(from, from + PAGE_SIZE);
}

/**
 * `1 – 50 of 2649` — their sentence, with our dash.
 *
 * ⚠️ **AN EN DASH, NOT A HYPHEN.** Everything else in this product that spans two numbers uses
 * one (`rangeLabel`, `appWeekRange`, the difficulty bands), and a hyphen here would be a second
 * spelling of one idea on a screen two clicks from the others.
 * ⚠️ **AND THE LAST PAGE SAYS WHAT IT HOLDS, not what a full page would hold** — `101 – 142 of
 * 142`, never `101 – 150`.
 */
export function pageLabel(total: number, page: number): string {
  if (total === 0) return "Nothing to show";
  const p = clampPage(page, total);
  const from = (p - 1) * PAGE_SIZE + 1;
  const to = Math.min(total, p * PAGE_SIZE);
  return `${String(from)} – ${String(to)} of ${String(total)}`;
}

/* ── The filter ─────────────────────────────────────────────────────────────────────────────── */

/**
 * The two facets a row is asked about in BOTH places a coach filters a library: the exercises
 * page's chip row, and W32(3)'s filter popover inside the workout builder's rail.
 *
 * 🔴 IT IS A SHARED FUNCTION RATHER THAN A SHARED NUMBER OR A COPIED CLAUSE, and the reason is
 * this repo's defining bug class: two components enforcing one concept with two spellings, each
 * internally correct, with nothing to catch the disagreement. `exerciseShelf.filterShelf` CALLS this, so
 * the page and the rail cannot answer "does this row match the Barbell chip" differently.
 *
 * ⚠️ IT TAKES EXACTLY THE TWO FACETS THE POPOVER OFFERS, and that is deliberate rather than a
 * first instalment. A matcher that accepted the whole `LibraryFilter` and silently skipped the
 * five facets the builder's row cannot see (`pattern`, `muscle`, `difficulty`, `hasVideo`,
 * `recordsNothing`) would answer a NARROWER question than it was asked, in the flattering
 * direction — the shape this codex calls silence-is-not-success. Two in, two asked.
 *
 * ⚠️ `equipment` IS NULLABLE HERE because the builder's row holds `string[] | null` where the
 * page's holds `string[]`. The shared side takes the wider type, so the narrower caller is the
 * one that widens — never the other way, which would make the page's rows unassignable.
 * A row with NO equipment recorded is excluded by an equipment chip, the same honest answer the
 * category clause already gives: the coach asked for Barbell, and this row has never said.
 */
export function matchesFacets(
  row: { readonly equipment: readonly string[] | null; readonly category: string | null },
  f: { readonly equipment: string | null; readonly category: string | null },
): boolean {
  if (f.equipment !== null && !(row.equipment ?? []).includes(f.equipment)) return false;
  /* ⚠️ AN UNCATEGORISED ROW IS EXCLUDED BY A CATEGORY CHIP, the same honest answer the
     difficulty band already gives: the coach asked for Strength, and this row has never been
     categorised, so the screen does not know whether it belongs. */
  if (f.category !== null && row.category !== f.category) return false;
  return true;
}

/* ── "Last used", derived ────────────────────────────────────────────────────────────────────── */

/**
 * When the coach last had each library exercise in an ASSIGNED workout.
 *
 * 🔴 THE ROSTER'S "LAST TRAINED" LESSON, ONE OBJECT OVER: read the thing that RECORDS SOMETHING
 * HAPPENING, never a status flag. `coach_assignments.scheduled_date` is a date the coach put on a
 * day; `status` is a word a button sets, and on this database 7 rows carry `completed_at` against
 * 52 that say `status='completed'`. A column built on the flag would tell a coach they had never
 * used exercises they use every week.
 *
 * ⚠️ IT WALKS THE ASSIGNED COPY, NOT THE TEMPLATE. Copy-on-assign (V7-2) means the assignment
 * points at a CLIENT-SCOPED COPY of the workout, and the copy's `coach_workout_exercises` rows
 * carry the same `exercise_id` as the template's. So the join is assignment → its own workout →
 * that workout's sections → their exercises, and a template a coach has built but never assigned
 * correctly reads as never used.
 *
 * ⚠️ A FUTURE DATE COUNTS, DELIBERATELY. The question this column answers is *is this row dead
 * weight in my library* — and something scheduled for next Tuesday plainly is not. Excluding the
 * future would file the rows a coach is actively programming as the most forgotten ones, which is
 * the exact opposite of what the column exists to surface.
 *
 * ⚠️ THREE FLAT READS, NO NESTED EMBED. A PostgREST embed comes back as an OBJECT or an ARRAY by
 * inferred relationship and arrives UNORDERED; three ID lists joined here cannot be misread that
 * way, and this function can be executed by a test.
 */
export function lastUsedByExercise(
  assignments: readonly { workout_id: string; scheduled_date: string | null }[],
  sections: readonly { id: string; workout_id: string }[],
  workoutExercises: readonly { exercise_id: string; section_id: string }[],
): Map<string, string> {
  const workoutOfSection = new Map(sections.map((s) => [s.id, s.workout_id]));

  // workout id -> the exercises it holds
  const exercisesOfWorkout = new Map<string, Set<string>>();
  for (const we of workoutExercises) {
    const workoutId = workoutOfSection.get(we.section_id);
    if (workoutId === undefined) continue; // a section this read did not return
    const set = exercisesOfWorkout.get(workoutId) ?? new Set<string>();
    set.add(we.exercise_id);
    exercisesOfWorkout.set(workoutId, set);
  }

  const out = new Map<string, string>();
  for (const a of assignments) {
    // ⚠️ A NULL DATE IS SKIPPED, NOT TREATED AS TODAY. `scheduled_date` is nullable; a row without
    // one is an assignment whose day this read cannot state, and inventing one would put a row at
    // the top of "most recently used" on the strength of a missing value.
    if (a.scheduled_date === null) continue;
    for (const exerciseId of exercisesOfWorkout.get(a.workout_id) ?? []) {
      const seen = out.get(exerciseId);
      // ISO dates compare correctly as strings — same length, most significant first.
      if (seen === undefined || a.scheduled_date > seen) out.set(exerciseId, a.scheduled_date);
    }
  }
  return out;
}

/* ── Deleting, and what the database will not let a coach do ─────────────────────────────────── */

/**
 * Which of the picked rows can actually go.
 *
 * 🔴 MEASURED ON THE LIVE SCHEMA, 2026-09-18 — `coach_exercises` is referenced by FIVE foreign
 * keys and the split is not a guess:
 *   * `coach_workout_exercises` · `exercise_pr_history` · `workout_session_exercises` — **RESTRICT**
 *   * `exercise_coverage` · `restricted_demo_videos` — CASCADE (derived rows, they go with it)
 * So a movement that is in a workout, that a client has ever LOGGED, or that carries a personal
 * record CANNOT be deleted, and the database is what says so. That is the right design and it is
 * already in place; what was missing is a screen that says it BEFORE the coach presses the button
 * rather than handing them a `23503`.
 *
 * ⚠️ THIS ANSWERS ONE OF THE THREE, AND SAYS SO. `usedInWorkouts` is built from the same
 * `coach_workout_exercises` read the `Last used` column already needs — free, and it covers the
 * case a coach hits constantly. The other two (a logged set, a personal record) are NOT read by
 * this screen, so a row can pass this check and still be refused; `deleteRefusal` is what turns
 * that refusal into a sentence instead of an error code. Claiming otherwise would be the screen
 * promising something it has not measured.
 */
export function splitDeletable(
  picked: readonly LibraryRow[],
  usedInWorkouts: ReadonlySet<string>,
): { deletable: LibraryRow[]; blocked: LibraryRow[] } {
  const deletable: LibraryRow[] = [];
  const blocked: LibraryRow[] = [];
  for (const r of picked) (usedInWorkouts.has(r.id) ? blocked : deletable).push(r);
  return { deletable, blocked };
}

/**
 * A refusal a coach can act on, from a Postgres error.
 *
 * 🔴 THE RULE IS THE ONE HIS WIFE PAID FOR: an internal error shown to a person is a dead button
 * with words on it. `23503` reaching the screen as *"insert or update on table … violates foreign
 * key constraint"* names a constraint nobody outside this repo has heard of, about a row they
 * cannot see, and offers nothing to do next.
 *
 * ⚠️ AND AN UNKNOWN CAUSE SAYS SO rather than guessing a specific one. A sentence that confidently
 * names the wrong reason is worse than one that admits it does not know, because the coach acts
 * on it.
 */
export function deleteRefusal(err: { code?: string | null; message?: string | null } | null): string | null {
  if (err === null) return null;
  if (err.code === "23503") {
    return "Some of these are still in use — in a workout, in a client's logged session, or "
      + "behind a personal record. Remove them from those first, and they can be deleted.";
  }
  const message = (err.message ?? "").trim();
  return message === "" ? "That could not be deleted. Nothing was changed." : message;
}

/** The exercises that appear in any workout — derived from the read `Last used` already needs. */
export function usedInWorkouts(
  workoutExercises: readonly { exercise_id: string }[],
): Set<string> {
  return new Set(workoutExercises.map((w) => w.exercise_id));
}
